import type { StoreObject, TypePolicies } from '@apollo/client';

/**
 * The app-wide Apollo type policies, in their own side-effect-free module so
 * tests can build `new InMemoryCache({ typePolicies })` with exactly the
 * production configuration instead of a hand-copied subset that drifts from it.
 * `src/apollo.ts` (which wires auth, error toasts and the http link on import)
 * consumes this object as-is.
 */
export const typePolicies: TypePolicies = {
  Query: {
    fields: {
      portfolio: { merge: true },
      portfolioV2: { keyArgs: ['teamId', 'portfolioId'], merge: true },
      team: { merge: true },
      content: { merge: true },
      portfolioV2Meta: { keyArgs: ['teamId', 'portfolioId'] },
    },
  },
  BudgetProposal: { keyFields: ['optimizationId'] },
  // Disable normalization for filterListType as they can often be duplicates, leading into weird integration issues
  filterListType: { keyFields: false },

  // ── Content Labels v2 ──────────────────────────────────────────────────────
  // The list, the stage counts and every stage page all answer through
  // `listContentLabelCategories(teamId)` — the only root field — so they write the
  // same category objects. (Not the same root ARRAY: the stage reads store theirs
  // under their own `@connection` key, see `contentLabels/stageReads.ts`.) Keyed by
  // their uuid ids (a DB UUID column, so globally unique and safe as a single key
  // field), each document merges its fields into one entity instead of keeping an
  // inline copy of its own, and a mutation that returns a category or value
  // updates every read.
  ContentLabelCategory: { keyFields: ['contentLabelCategoryId'] },
  ContentLabelCategoryValue: { keyFields: ['contentLabelCategoryValueId'] },
  // Has no id of its own. Without a merge, the counts document's `counts` and a page
  // document's `labeled(first: 20, …)` — separate fields — would replace each other
  // wholesale on every write.
  //
  // Deliberately NOT `relayStylePagination`: the pages are list-shaped (every
  // category answers the same cursor), and the tables page by REPLACING the page
  // through variables, so each argument set simply keeps its own page.
  // `ContentV2` / `ContentLabelAssignment` stay unkeyed for the same reason: they
  // are per-read snapshots nested under a category's stage.
  ContentsByAssignmentStage: { merge: true },

  // ── Trend signals ──────────────────────────────────────────────────────────
  // The list reads (metadata) and the per-signal data read (dataPoints per date range) describe the
  // same signal: keyFields folds them into one entity. `timeSeries` is un-keyed and the documents
  // select different subsets of it, so its fields merge; `dataPoints` stays keyed by its (dateFrom,
  // dateTo) args. A series that was replaced or re-ingested starts over: its cached points are gone.
  TrendSignal: {
    keyFields: ['trendSignalId'],
    fields: { timeSeries: { merge: mergeTrendSignalTimeSeries } },
  },
};

// Compared only when both writes selected the field: not every document asks for `ingestedAt`.
const SERIES_IDENTITY_FIELDS = ['status', 'ingestedAt', 'lastDate'] as const;

function mergeTrendSignalTimeSeries(existing: StoreObject | undefined, incoming: StoreObject): StoreObject {
  if (!existing) return incoming;
  const replaced = SERIES_IDENTITY_FIELDS.some(
    (field) => existing[field] !== undefined && incoming[field] !== undefined && existing[field] !== incoming[field],
  );
  return replaced ? incoming : { ...existing, ...incoming };
}
